import { useEffect, useRef, useState } from "react";
import { Paperclip, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { downloadBlob } from "../FollowupFields";
import { Card, inputCls, btnPrimary, errMsg } from "./ui";

export function MessagesPanel({ base, side, title }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  useEffect(() => { api.get(base).then(({ data }) => setMsgs(data.messages)).catch(() => {}); }, [base]);
  const send = async () => {
    const fd = new FormData();
    fd.append("text", text);
    if (file) fd.append("file", file);
    setBusy(true);
    try {
      setMsgs((await api.post(base, fd)).data.messages);
      setText(""); setFile(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-0 overflow-hidden max-w-3xl" data-testid="messages-panel">
      <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-[#0B2545]">{title}</div>
      <div className="p-5 space-y-4 bg-slate-50/60 max-h-[480px] overflow-y-auto">
        {msgs.length === 0 && <p className="text-sm text-slate-500">No messages yet.</p>}
        {msgs.map((m) => {
          const mine = m.from === side;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : ""}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${mine ? "bg-[#0B2545] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                <p className={`text-[11px] mb-1 ${mine ? "text-sky-200" : "text-slate-400"}`}>{m.author} · {new Date(m.created_at).toLocaleString("en-GB")}</p>
                {m.text}
                {m.attachment && (
                  <button type="button" className="mt-2 text-xs flex items-center gap-1 underline" data-testid={`message-attachment-${m.id}`}
                    onClick={() => downloadBlob(api, `/institutions/messages/${m.id}/attachment`, m.attachment.name).catch(() => toast.error("Download failed"))}>
                    <Paperclip className="w-3 h-3" /> {m.attachment.name}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-4 border-t border-slate-100 flex gap-2 items-center">
        <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="message-file-input" />
        <button type="button" className={`p-2 ${file ? "text-[#1C6FE3]" : "text-slate-500"}`} title={file ? file.name : "Attach PDF / image"} onClick={() => fileRef.current.click()} data-testid="message-attach-btn"><Paperclip className="w-4 h-4" /></button>
        <input className={inputCls} placeholder={file ? `Attached: ${file.name}` : "Write a message…"} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="message-input" />
        <button type="button" className={btnPrimary} onClick={send} disabled={busy} data-testid="message-send-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </div>
    </Card>
  );
}
